import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastProvider } from '@/components/ui/toast';
import { PlayerPage } from '@/pages/PlayerPage';
import { ProvisionPage } from '@/pages/ProvisionPage';
import { TvInstallPage } from '@/pages/TvInstallPage';

// Lazily load the entire admin tree so the player route does not pull in
// auth/tenant providers, the admin shell, or the editor surface area.
const AdminApp = lazy(() =>
  import('@/AdminApp').then((m) => ({ default: m.AdminApp })),
);

export function App() {
  return (
    <ToastProvider>
      <Routes>
        {/* The player route runs without auth, tenant context, or admin chrome.
            It is intentionally placed outside of any auth/tenant providers so
            it can hydrate from cache and play offline even on first paint. */}
        <Route path="/play/:screenId" element={<PlayerPage />} />
        <Route path="/player/:screenId" element={<PlayerPage />} />

        {/* /provision is the boot URL for the Raspberry Pi appliance image.
            It runs anonymously, displays a pairing code, polls the Pages
            Function, and redirects to /play/:screenId once an admin claims
            the code. Sits outside any auth/tenant providers for the same
            reason as /play/:screenId. */}
        <Route path="/provision" element={<ProvisionPage />} />

        {/* Public TV install landing page. /tv shows both Fire TV and
            Google TV cards; the platform-specific subroutes highlight
            one card at a time so a customer pointed at /tv/google-tv
            from the admin doesn't have to scroll past the other one. */}
        <Route path="/tv" element={<TvInstallPage />} />
        <Route path="/tv/fire-tv" element={<TvInstallPage highlight="fire-tv" />} />
        <Route path="/tv/google-tv" element={<TvInstallPage highlight="google-tv" />} />

        {/* Everything else is admin/auth and is code-split into its own chunk. */}
        <Route
          path="*"
          element={
            <Suspense fallback={<AppShellFallback />}>
              <AdminApp />
            </Suspense>
          }
        />
      </Routes>
    </ToastProvider>
  );
}

function AppShellFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

// Catch unknown routes from inside the admin tree (kept for backwards compat;
// the admin shell handles its own subroutes).
export function NotFound() {
  return <Navigate to="/app" replace />;
}
